import type { Delivery, IDeliveryLine, InventoryLevel, UntrackedMaterial } from '../interfaces/ILogistics.js';
import { fold } from '../utils/text.js';

/** The day an untracked material was due has come (`today` is the site's
 *  `YYYY-MM-DD`). */
export function untrackedDue(material: UntrackedMaterial, today: string): boolean {
  return material.dueDate != null && material.dueDate <= today;
}

/** An untracked material should physically be on site: it arrived, or it is
 *  due. Before that its "Add stock" is a to-do nobody can do — one rule for
 *  the Inventory row and the delivery drawer alike. */
export function untrackedOnSite(material: UntrackedMaterial, today: string): boolean {
  return material.deliveredAt != null || untrackedDue(material, today);
}

/** What a delivery line's material offers to reconcile it into stock — the
 *  Inventory row's own stock actions, and only those. */
export type LineStockAction =
  | { kind: 'synced' }
  | { kind: 'sync'; level: InventoryLevel }
  | { kind: 'addStock'; material: UntrackedMaterial };

/** The site's stock, keyed by folded material name — built once per payload so
 *  a line's lookup is a Map hit, not a fold of every inventory name. */
export interface IStockIndex {
  levelsByName: Map<string, InventoryLevel>;
  untrackedByName: Map<string, UntrackedMaterial>;
}

/** Index `items` by folded name, the FIRST of a name winning (as `find` did). */
function indexByName<T extends { materialName: string }>(items: T[]): Map<string, T> {
  const byName = new Map<string, T>();
  for (const item of items) {
    const foldedName = fold(item.materialName);
    if (!byName.has(foldedName)) byName.set(foldedName, item);
  }
  return byName;
}

export function indexStockByName(levels: InventoryLevel[], untracked: UntrackedMaterial[]): IStockIndex {
  return { levelsByName: indexByName(levels), untrackedByName: indexByName(untracked) };
}

/**
 * The stock action for one line of `delivery`: already synced; Sync, when the
 * material is tracked and this order has arrived; Add stock, when it is not
 * tracked yet and its Inventory row offers it. Null otherwise — a tracked
 * material still in flight has nothing to reconcile yet.
 */
export function lineStockAction(
  delivery: Delivery,
  line: IDeliveryLine,
  stock: IStockIndex,
  today: string,
): LineStockAction | null {
  if (line.isSynced) return { kind: 'synced' };
  const foldedName = fold(line.itemName);
  const level = stock.levelsByName.get(foldedName);
  if (level) return delivery.deliveredAt != null ? { kind: 'sync', level } : null;
  const material = stock.untrackedByName.get(foldedName);
  return material && untrackedOnSite(material, today) ? { kind: 'addStock', material } : null;
}
